import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { enqueue } from "@/utils/offlineQueue";
import {
  ChevronLeft, Brain, Camera, Save, AlertTriangle, Loader2,
  CheckCircle2, FileText, X, Printer, ChevronRight, WifiOff, PenLine, ShieldCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TISSUE_TYPES = ["Granulação", "Epitelização", "Fibrina", "Necrose seca", "Necrose úmida", "Esfacelo"];
const INFECTION_SIGNS = ["Eritema", "Calor local", "Edema", "Exsudato purulento", "Febre", "Celulite", "Drenagem"];
const RISK_LABELS = { critico: "Crítico", alto: "Alto", medio: "Médio", baixo: "Baixo" };
const RISK_COLORS = {
  critico: "bg-red-100 text-red-800 border-red-200",
  alto: "bg-orange-100 text-orange-800 border-orange-200",
  medio: "bg-yellow-100 text-yellow-800 border-yellow-200",
  baixo: "bg-green-100 text-green-800 border-green-200",
};

const INCIDENCES = ["Vista frontal", "Lateral esquerda", "Lateral direita"];

const STEPS = [
  { id: 1, label: "Descrição" },
  { id: 2, label: "Fotos" },
  { id: 3, label: "Análise IA" },
  { id: 4, label: "Curativo" },
];

export default function NewEvolutionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const woundId = searchParams.get("woundId");
  const patientId = searchParams.get("patientId");
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [wound, setWound] = useState(null);
  const [patient, setPatient] = useState(null);
  const { isOnline } = useOnlineStatus();

  // Step 1: clinical data
  const [form, setForm] = useState({
    wound_id: woundId || "",
    patient_id: patientId || "",
    date: new Date().toISOString().split("T")[0],
    length_cm: "", width_cm: "", depth_cm: "",
    tissue_types: [], exudate_amount: "", exudate_type: "",
    odor: "ausente", perilesional_skin: "",
    pain_score: 0, infection_signs: [],
    notes: "", vital_signs: {}, wound_specific: {},
    glycemic_control: "", npuap_stage: "",
    glucose_readings: [],
  });

  // Helpers para glicemias
  const addGlucoseReading = () => setForm(p => ({
    ...p,
    glucose_readings: [
      ...p.glucose_readings,
      { date: new Date().toISOString().split("T")[0], period: "manha", value_mgdl: "", observation: "" }
    ]
  }));
  const updateGlucoseReading = (i, field, value) => setForm(p => {
    const readings = [...p.glucose_readings];
    readings[i] = { ...readings[i], [field]: value };
    return { ...p, glucose_readings: readings };
  });
  const removeGlucoseReading = (i) => setForm(p => ({
    ...p, glucose_readings: p.glucose_readings.filter((_, idx) => idx !== i)
  }));

  // Step 2: photos (up to 3)
  const [photos, setPhotos] = useState([null, null, null]); // {file, preview, incidence}
  const fileRefs = [useRef(null), useRef(null), useRef(null)];

  // Step 3: AI analysis
  const [savedEvolutionId, setSavedEvolutionId] = useState(null);
  const [savingEvolution, setSavingEvolution] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState("");

  // Step 4: dressing + signature confirmation modal
  const [dressingDescription, setDressingDescription] = useState("");
  const [showSignModal, setShowSignModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Materials
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState({}); // {material_id: quantity}

  const toggleMaterial = (mat) => {
    setSelectedMaterials(prev => {
      if (prev[mat.material_id]) {
        const next = { ...prev };
        delete next[mat.material_id];
        return next;
      }
      return { ...prev, [mat.material_id]: 1 };
    });
  };
  const setMatQty = (id, qty) => {
    setSelectedMaterials(prev => ({ ...prev, [id]: Math.max(1, parseInt(qty) || 1) }));
  };

  useEffect(() => {
    if (woundId) {
      axios.get(`${API}/wounds/${woundId}`, { withCredentials: true })
        .then(r => setWound(r.data)).catch(() => {});
    }
    if (patientId) {
      axios.get(`${API}/patients/${patientId}`, { withCredentials: true })
        .then(r => setPatient(r.data)).catch(() => {});
    }
    // Load municipality materials for step 4
    axios.get(`${API}/municipality-materials`, { withCredentials: true })
      .then(r => setAvailableMaterials(r.data.filter(m => m.is_active)))
      .catch(() => {});
  }, [woundId, patientId]);

  const setField = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const toggleArray = (field, val) => {
    setForm(p => ({
      ...p,
      [field]: p[field].includes(val) ? p[field].filter(x => x !== val) : [...p[field], val]
    }));
  };

  const handlePhotoChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPhotos(prev => {
      const next = [...prev];
      next[index] = { file, preview, incidence: INCIDENCES[index] };
      return next;
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    if (fileRefs[index].current) fileRefs[index].current.value = "";
  };

  // Transition from Step 2 to Step 3: save evolution + upload photos + trigger AI
  const handleGoToAI = async () => {
    setSavingEvolution(true);
    setAiError("");
    try {
      // 1) Save evolution if not yet saved
      let evoId = savedEvolutionId;
      if (!evoId) {
        const payload = {
          ...form,
          length_cm: parseFloat(form.length_cm) || 0,
          width_cm: parseFloat(form.width_cm) || 0,
          depth_cm: parseFloat(form.depth_cm) || 0,
          pain_score: parseInt(form.pain_score) || 0,
        };
        const resp = await axios.post(`${API}/evolutions`, payload, { withCredentials: true });
        evoId = resp.data.evolution_id;
        setSavedEvolutionId(evoId);
      }

      // 2) Upload photos sequentially
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        if (!p) continue;
        const fd = new FormData();
        fd.append("file", p.file);
        await axios.post(
          `${API}/photos/upload?wound_id=${woundId}&patient_id=${patientId}&evolution_id=${evoId}&notes=${encodeURIComponent(p.incidence)}`,
          fd,
          { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      setStep(3);

      // 3) Trigger AI analysis automatically
      setAnalyzing(true);
      const result = await axios.post(`${API}/ai/analyze`, {
        evolution: {
          ...form,
          evolution_id: evoId,
          wound_id: woundId,
          length_cm: parseFloat(form.length_cm) || 0,
          width_cm: parseFloat(form.width_cm) || 0,
          depth_cm: parseFloat(form.depth_cm) || 0,
        },
        wound: wound || { wound_type: "", location: "", start_date: "" },
        patient: patient || { name: "", comorbidities: [] },
      }, { withCredentials: true });
      setAiResult(result.data);
    } catch (e) {
      console.error(e);
      setAiError("Erro ao salvar evolução ou analisar. Tente novamente.");
    } finally {
      setSavingEvolution(false);
      setAnalyzing(false);
    }
  };

  // Step 4: save dressing description and finish
  const handleFinish = async () => {
    setSaving(true);
    const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
    try {
      if (!isOnline) {
        // Queue offline — photos skipped (need connectivity for binary upload)
        const payload = {
          ...form,
          length_cm: parseFloat(form.length_cm) || 0,
          width_cm: parseFloat(form.width_cm) || 0,
          depth_cm: parseFloat(form.depth_cm) || 0,
          pain_score: parseInt(form.pain_score) || 0,
          dressing_description: dressingDescription,
        };
        enqueue({ method: "POST", url: `${API_URL}/evolutions`, data: payload });
        navigate(`/wounds/${woundId}`, { state: { offlineQueued: true } });
        return;
      }
      if (savedEvolutionId && dressingDescription.trim()) {
        const materialsUsed = Object.entries(selectedMaterials).map(([matId, qty]) => {
          const mat = availableMaterials.find(m => m.material_id === matId);
          return { material_id: matId, material_name: mat?.name || "", category: mat?.category || "", unit: mat?.unit || "un", quantity: qty };
        });
        await axios.patch(`${API_URL}/evolutions/${savedEvolutionId}`, {
          dressing_description: dressingDescription,
          materials_used: materialsUsed,
        }, { withCredentials: true });
      } else if (!savedEvolutionId) {
        const payload = {
          ...form,
          length_cm: parseFloat(form.length_cm) || 0,
          width_cm: parseFloat(form.width_cm) || 0,
          depth_cm: parseFloat(form.depth_cm) || 0,
          pain_score: parseInt(form.pain_score) || 0,
          dressing_description: dressingDescription,
        };
        await axios.post(`${API_URL}/evolutions`, payload, { withCredentials: true });
      }
      navigate(`/wounds/${woundId}`);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const WOUND_LABELS = { ulcera_venosa: "Úlcera Venosa", pe_diabetico: "Pé Diabético", lesao_por_pressao: "Lesão por Pressão" };

  const isDiabetic = wound?.wound_type === "pe_diabetico" || patient?.comorbidities?.includes("Diabetes Mellitus");
  const isPressureWound = wound?.wound_type === "lesao_por_pressao";
  const photosSelected = photos.filter(Boolean).length;

  return (
    <Layout>
      <div data-testid="new-evolution-page" className="max-w-2xl mx-auto pb-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 animate-fade-in">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate(`/wounds/${woundId}`)} className="p-2 rounded-lg hover:bg-secondary transition-all">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>Nova Evolução</h1>
            {wound && <p className="text-sm text-muted-foreground">{WOUND_LABELS[wound.wound_type]} · {patient?.name}</p>}
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-6 animate-fade-in">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step > s.id ? "bg-primary text-white" : step === s.id ? "bg-primary text-white ring-4 ring-primary/20" : "bg-secondary text-muted-foreground"
                }`}>
                  {step > s.id ? <CheckCircle2 className="w-4 h-4" /> : s.id}
                </div>
                <span className={`text-xs mt-1 font-medium ${step === s.id ? "text-primary" : "text-muted-foreground"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mb-5 mx-1 ${step > s.id ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* ===== STEP 1: Descrição ===== */}
        {step === 1 && (
          <div className="space-y-5 animate-fade-in">
            {/* Date */}
            <div className="bg-white rounded-xl border border-border p-5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Data da Avaliação</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setField("date", e.target.value)}
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="evolution-date-input"
              />
            </div>

            {/* Measurements */}
            <div className="bg-white rounded-xl border border-border p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Medidas da Lesão (cm)</h2>
              <div className="grid grid-cols-3 gap-3">
                {[["length_cm", "Comprimento"], ["width_cm", "Largura"], ["depth_cm", "Profundidade"]].map(([f, l]) => (
                  <div key={f}>
                    <label className="text-xs text-muted-foreground">{l}</label>
                    <input
                      type="number" step="0.1" min="0"
                      value={form[f]}
                      onChange={e => setField(f, e.target.value)}
                      placeholder="0.0"
                      className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid={`evolution-${f}-input`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Tissue types */}
            <div className="bg-white rounded-xl border border-border p-5 space-y-3">
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Tipos de Tecido</h2>
              <div className="grid grid-cols-2 gap-2">
                {TISSUE_TYPES.map(t => (
                  <label key={t} className="flex items-center gap-2 p-2.5 border border-border rounded-lg cursor-pointer hover:bg-secondary/50 transition-all">
                    <input type="checkbox" checked={form.tissue_types.includes(t)} onChange={() => toggleArray("tissue_types", t)} className="w-4 h-4 accent-primary" data-testid={`tissue-${t.replace(/\s/g, "-")}`} />
                    <span className="text-xs text-foreground">{t}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Exudate */}
            <div className="bg-white rounded-xl border border-border p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Exsudato e Odor</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Quantidade</label>
                  <select value={form.exudate_amount} onChange={e => setField("exudate_amount", e.target.value)}
                    className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="exudate-amount-select">
                    <option value="">Selecionar</option>
                    <option value="ausente">Ausente</option>
                    <option value="escasso">Escasso</option>
                    <option value="moderado">Moderado</option>
                    <option value="abundante">Abundante</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Tipo</label>
                  <select value={form.exudate_type} onChange={e => setField("exudate_type", e.target.value)}
                    className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="exudate-type-select">
                    <option value="">Selecionar</option>
                    <option value="seroso">Seroso</option>
                    <option value="serossanguinolento">Serossanguinolento</option>
                    <option value="sanguinolento">Sanguinolento</option>
                    <option value="purulento">Purulento</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Odor</label>
                <div className="flex gap-3 mt-1">
                  {["ausente", "presente"].map(v => (
                    <label key={v} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" value={v} checked={form.odor === v} onChange={() => setField("odor", v)} className="accent-primary" />
                      <span className="text-sm capitalize">{v}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Pele Perilesional</label>
                <input type="text" value={form.perilesional_skin} onChange={e => setField("perilesional_skin", e.target.value)}
                  placeholder="Ex: íntegra, macerada, eritematosa..."
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="perilesional-skin-input" />
              </div>
            </div>

            {/* Pain and infection */}
            <div className="bg-white rounded-xl border border-border p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Dor e Infecção</h2>
              <div>
                <label className="text-xs text-muted-foreground">Dor (EVA 0-10): {form.pain_score}</label>
                <input type="range" min="0" max="10" value={form.pain_score} onChange={e => setField("pain_score", parseInt(e.target.value))}
                  className="mt-2 w-full accent-primary" data-testid="pain-score-slider" />
                <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
                  <span>Sem dor (0)</span><span>Máxima (10)</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">Sinais de Infecção</label>
                <div className="grid grid-cols-2 gap-2">
                  {INFECTION_SIGNS.map(s => (
                    <label key={s} className={`flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-all ${
                      form.infection_signs.includes(s) ? "border-destructive/50 bg-destructive/5" : "border-border hover:bg-secondary/50"
                    }`}>
                      <input type="checkbox" checked={form.infection_signs.includes(s)} onChange={() => toggleArray("infection_signs", s)}
                        className="w-4 h-4 accent-red-500" data-testid={`infection-${s.replace(/\s/g, "-")}`} />
                      <span className="text-xs text-foreground">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Glycemic control — diabetics */}
            {isDiabetic && (
              <div className="bg-white rounded-xl border border-primary/20 p-5 space-y-3 animate-fade-in">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Controle Glicêmico</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Diabetes</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "adequado", label: "Adequado", cls: "bg-green-50 border-green-300 text-green-800" },
                    { value: "inadequado", label: "Inadequado", cls: "bg-red-50 border-red-300 text-red-800" },
                    { value: "não_verificado", label: "Não verificado", cls: "bg-gray-50 border-gray-300 text-gray-600" },
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-center justify-center p-3 border rounded-xl cursor-pointer text-xs font-medium transition-all ${form.glycemic_control === opt.value ? opt.cls : "border-border text-muted-foreground hover:bg-secondary/50"}`}
                      data-testid={`evo-glycemic-${opt.value.replace(/[^a-z]/g, "-")}`}>
                      <input type="radio" value={opt.value} checked={form.glycemic_control === opt.value} onChange={() => setField("glycemic_control", opt.value)} className="sr-only" />
                      {opt.label}
                    </label>
                  ))}
                </div>

                {/* Glicemias medidas */}
                <div className="pt-2 border-t border-border/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Glicemias Medidas</span>
                    <button
                      type="button"
                      onClick={addGlucoseReading}
                      className="text-xs flex items-center gap-1 text-primary hover:underline"
                      data-testid="add-glucose-btn"
                    >
                      + Adicionar leitura
                    </button>
                  </div>

                  {form.glucose_readings.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">Nenhuma glicemia registrada. Clique em "+ Adicionar leitura" para incluir.</p>
                  )}

                  {form.glucose_readings.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 animate-fade-in" data-testid={`glucose-reading-${i}`}>
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <input
                          type="date"
                          value={r.date}
                          onChange={e => updateGlucoseReading(i, "date", e.target.value)}
                          className="h-9 border border-border rounded-lg px-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid={`glucose-date-${i}`}
                        />
                        <select
                          value={r.period}
                          onChange={e => updateGlucoseReading(i, "period", e.target.value)}
                          className="h-9 border border-border rounded-lg px-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid={`glucose-period-${i}`}
                        >
                          <option value="manha">Manhã</option>
                          <option value="tarde">Tarde</option>
                          <option value="noite">Noite</option>
                        </select>
                        <div className="relative">
                          <input
                            type="number"
                            min="20"
                            max="600"
                            value={r.value_mgdl}
                            onChange={e => updateGlucoseReading(i, "value_mgdl", parseInt(e.target.value) || "")}
                            placeholder="mg/dL"
                            className={`h-9 w-full border rounded-lg px-2 pr-14 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 ${r.value_mgdl > 300 ? "border-red-400 bg-red-50" : r.value_mgdl > 180 ? "border-yellow-400 bg-yellow-50" : "border-border"}`}
                            data-testid={`glucose-value-${i}`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">mg/dL</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeGlucoseReading(i)} className="mt-1 p-1.5 rounded hover:bg-red-50 hover:text-red-600 text-muted-foreground transition-all">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}

                  {form.glucose_readings.some(r => r.value_mgdl > 300) && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-2 animate-fade-in">
                      <span className="text-xs text-red-800 font-medium">⚠ Glicemia acima de 300 mg/dL detectada — avaliação urgente recomendada</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* NPUAP — pressure wounds */}
            {isPressureWound && (
              <div className="bg-white rounded-xl border border-border p-5 space-y-3 animate-fade-in">
                <span className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Estágio NPUAP/EPUAP</span>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "1", label: "Estágio 1", desc: "Eritema não branqueável" },
                    { value: "2", label: "Estágio 2", desc: "Perda parcial da espessura" },
                    { value: "3", label: "Estágio 3", desc: "Perda total da espessura" },
                    { value: "4", label: "Estágio 4", desc: "Perda total + tecido exposto" },
                    { value: "nao_classificavel", label: "Não classificável", desc: "Profundidade obscurecida" },
                    { value: "dti", label: "DTI", desc: "Lesão de tecido profundo" },
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-start gap-2 p-3 border rounded-xl cursor-pointer transition-all ${form.npuap_stage === opt.value ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}
                      data-testid={`npuap-stage-evo-${opt.value}`}>
                      <input type="radio" value={opt.value} checked={form.npuap_stage === opt.value} onChange={() => setField("npuap_stage", opt.value)} className="mt-0.5 accent-primary" />
                      <div>
                        <p className={`text-xs font-semibold ${form.npuap_stage === opt.value ? "text-primary" : "text-foreground"}`}>{opt.label}</p>
                        <p className="text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="bg-white rounded-xl border border-border p-5">
              <h2 className="text-sm font-semibold text-foreground mb-3" style={{ fontFamily: 'Manrope' }}>Observações Clínicas</h2>
              <textarea value={form.notes} onChange={e => setField("notes", e.target.value)}
                placeholder="Anotações adicionais, contexto clínico relevante..."
                rows={3} className="w-full border border-border rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="notes-textarea" />
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all"
              data-testid="step1-next-btn"
            >
              Próximo: Fotos <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ===== STEP 2: Fotos ===== */}
        {step === 2 && (
          <div className="space-y-5 animate-fade-in">
            <div className="bg-white rounded-xl border border-border p-5">
              <h2 className="text-sm font-semibold text-foreground mb-1" style={{ fontFamily: 'Manrope' }}>Registro Fotográfico</h2>
              <p className="text-xs text-muted-foreground mb-4">Adicione até 3 fotos em incidências diferentes para melhor documentação clínica.</p>

              <div className="space-y-4">
                {[0, 1, 2].map(i => (
                  <div key={i} className="border border-border rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-secondary/30 border-b border-border">
                      <span className="text-xs font-medium text-foreground">{INCIDENCES[i]}</span>
                      <span className="text-xs text-muted-foreground">Foto {i + 1} de 3</span>
                    </div>
                    {photos[i] ? (
                      <div className="relative">
                        <img src={photos[i].preview} alt={`Foto ${i + 1}`} className="w-full object-cover max-h-52" />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-all"
                          data-testid={`remove-photo-${i}`}
                        >
                          <X className="w-4 h-4 text-foreground" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileRefs[i].current?.click()}
                        className="w-full p-6 flex flex-col items-center gap-2 hover:bg-primary/5 transition-all"
                        data-testid={`photo-upload-btn-${i}`}
                      >
                        <Camera className="w-7 h-7 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Tirar foto ou selecionar arquivo</p>
                      </button>
                    )}
                    <input
                      ref={fileRefs[i]}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={e => handlePhotoChange(i, e)}
                      className="hidden"
                      data-testid={`photo-file-input-${i}`}
                    />
                  </div>
                ))}
              </div>

              {photosSelected > 0 && (
                <p className="text-xs text-primary mt-3 text-center">
                  {photosSelected} foto(s) selecionada(s)
                </p>
              )}
            </div>

            {aiError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-xs text-destructive">{aiError}</div>
            )}

            <button
              onClick={handleGoToAI}
              disabled={savingEvolution}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
              data-testid="step2-next-btn"
            >
              {savingEvolution ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
              {savingEvolution ? "Salvando e analisando..." : "Próximo: Análise de IA"}
            </button>
          </div>
        )}

        {/* ===== STEP 3: Análise de IA ===== */}
        {step === 3 && (
          <div className="space-y-5 animate-fade-in">
            {analyzing ? (
              <div className="bg-white rounded-xl border border-border p-10 flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Brain className="w-7 h-7 text-primary animate-pulse" />
                </div>
                <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Analisando dados clínicos...</p>
                <p className="text-xs text-muted-foreground text-center">A IA está avaliando os dados informados. Isso pode levar alguns segundos.</p>
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>
            ) : aiResult ? (
              <>
                {/* Risk badge + summary */}
                <div className={`bg-white rounded-xl border p-5 ${RISK_COLORS[aiResult.nivel_risco]?.includes("border") ? "" : "border-border"}`} data-testid="ai-result-panel">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-primary" />
                      <h2 className="text-sm font-semibold" style={{ fontFamily: 'Manrope' }}>Análise de IA</h2>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${RISK_COLORS[aiResult.nivel_risco]}`} data-testid="ai-risk-badge">
                      {RISK_LABELS[aiResult.nivel_risco] || aiResult.nivel_risco}
                    </span>
                  </div>

                  {aiResult.bandeiras_vermelhas?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                        <span className="text-xs font-bold text-red-700">Bandeiras Vermelhas</span>
                      </div>
                      {aiResult.bandeiras_vermelhas.map((f, i) => (
                        <p key={i} className="text-xs text-red-700">• {f}</p>
                      ))}
                    </div>
                  )}

                  <p className="text-sm text-foreground mb-3">{aiResult.resumo_clinico}</p>

                  {aiResult.achados_visuais && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.82V15.18a1 1 0 01-1.447.894L15 14M4 6h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
                        </svg>
                        <p className="text-xs font-semibold text-blue-700">Achados Visuais (Foto)</p>
                      </div>
                      <p className="text-xs text-blue-900">{aiResult.achados_visuais}</p>
                    </div>
                  )}

                  {aiResult.sugestao_protocolo && (
                    <div className="bg-primary/5 rounded-lg p-3 mb-3">
                      <p className="text-xs font-semibold text-primary mb-1">Sugestão de Protocolo e Curativo</p>
                      <p className="text-xs text-foreground whitespace-pre-line">{aiResult.sugestao_protocolo}</p>
                    </div>
                  )}

                  {aiResult.justificativa && (
                    <p className="text-xs text-muted-foreground italic">{aiResult.justificativa}</p>
                  )}

                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border italic">
                    Suporte à decisão clínica — não substitui avaliação profissional.
                  </p>
                </div>

                {/* Regulation Report */}
                {aiResult.necessita_encaminhamento && aiResult.relatorio_regulacao && (
                  <div className="bg-white rounded-xl border border-orange-200 p-5" data-testid="regulation-report-panel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-600" />
                        <h2 className="text-sm font-semibold text-orange-700" style={{ fontFamily: 'Manrope' }}>Relatório de Regulação</h2>
                      </div>
                      <button
                        onClick={() => {
                          const w = window.open("", "_blank");
                          w.document.write(`<html><head><title>Relatório de Regulação</title><style>body{font-family:monospace;padding:24px;white-space:pre-wrap;font-size:14px}</style></head><body>${aiResult.relatorio_regulacao}</body></html>`);
                          w.print();
                        }}
                        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 border border-orange-200 rounded-lg px-2.5 py-1.5 hover:bg-orange-50 transition-all"
                        data-testid="print-regulation-report-btn"
                      >
                        <Printer className="w-3.5 h-3.5" /> Imprimir
                      </button>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <pre className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed" data-testid="regulation-report-text">
                        {aiResult.relatorio_regulacao}
                      </pre>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl border border-border p-10 text-center">
                <AlertTriangle className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Análise não disponível. Clique em continuar.</p>
              </div>
            )}

            <button
              onClick={() => setStep(4)}
              disabled={analyzing}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
              data-testid="step3-next-btn"
            >
              Próximo: Registrar Curativo <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ===== STEP 4: Curativo ===== */}
        {step === 4 && (
          <div className="space-y-5 animate-fade-in">
            {!isOnline && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-2">
                <WifiOff className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <p className="text-xs text-orange-700">Sem conexão — a evolução será salva localmente e sincronizada automaticamente ao reconectar. Fotos precisarão ser adicionadas online.</p>
              </div>
            )}
            <div className="bg-white rounded-xl border border-border p-5">
              <h2 className="text-sm font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>Descrição do Curativo</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Descreva detalhadamente como foi realizado o curativo: técnica, coberturas utilizadas, procedimentos e intercorrências.
              </p>
              <textarea
                value={dressingDescription}
                onChange={e => setDressingDescription(e.target.value)}
                placeholder="Ex: Curativo realizado com SF 0,9% para limpeza. Aplicação de cobertura de alginato de cálcio sobre o leito da lesão com exsudato moderado seroso. Fixação com bandagem elástica. Paciente refere dor EVA 3/10 durante o procedimento, tolerado."
                rows={7}
                className="w-full border border-border rounded-lg px-3 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 leading-relaxed"
                data-testid="dressing-description-textarea"
              />
            </div>

            {/* Materiais utilizados */}
            {availableMaterials.length > 0 && (
              <div className="bg-white rounded-xl border border-border p-5">
                <h2 className="text-sm font-semibold text-foreground mb-1" style={{ fontFamily: "Manrope" }}>Materiais Utilizados</h2>
                <p className="text-xs text-muted-foreground mb-4">Selecione os materiais utilizados neste curativo e informe a quantidade.</p>
                <div className="space-y-4">
                  {["coberturas_primarias","coberturas_secundarias","limpeza","desbridamento","fixacao","outros"].map(cat => {
                    const catMats = availableMaterials.filter(m => m.category === cat);
                    if (!catMats.length) return null;
                    const catLabels = { coberturas_primarias: "Coberturas Primárias", coberturas_secundarias: "Coberturas Secundárias", limpeza: "Limpeza", desbridamento: "Desbridamento", fixacao: "Fixação", outros: "Outros" };
                    return (
                      <div key={cat}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{catLabels[cat]}</p>
                        <div className="space-y-2">
                          {catMats.map(mat => {
                            const checked = !!selectedMaterials[mat.material_id];
                            return (
                              <div key={mat.material_id}
                                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${checked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-secondary/30"}`}
                                onClick={() => toggleMaterial(mat)}
                                data-testid={`material-check-${mat.material_id}`}
                              >
                                <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${checked ? "bg-primary border-primary" : "border-border"}`}>
                                  {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                                </div>
                                <span className="text-sm flex-1 select-none">{mat.name}</span>
                                {checked && (
                                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => setMatQty(mat.material_id, (selectedMaterials[mat.material_id] || 1) - 1)}
                                      className="w-6 h-6 rounded-lg border border-border flex items-center justify-center text-xs hover:bg-secondary transition-all">-</button>
                                    <span className="text-sm font-medium w-8 text-center">{selectedMaterials[mat.material_id]}</span>
                                    <button onClick={() => setMatQty(mat.material_id, (selectedMaterials[mat.material_id] || 1) + 1)}
                                      className="w-6 h-6 rounded-lg border border-border flex items-center justify-center text-xs hover:bg-secondary transition-all">+</button>
                                    <span className="text-xs text-muted-foreground">{mat.unit}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Summary card */}
            <div className="bg-secondary/30 rounded-xl p-4 border border-border">
              <p className="text-xs font-semibold text-foreground mb-2">Resumo da Consulta</p>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Data: {form.date}</p>
                <p className="text-xs text-muted-foreground">Dimensões: {form.length_cm || "—"} × {form.width_cm || "—"} × {form.depth_cm || "—"} cm</p>
                <p className="text-xs text-muted-foreground">Fotos: {photosSelected} registrada(s)</p>
                {aiResult && (
                  <p className="text-xs text-muted-foreground">Risco IA: <span className="font-medium">{RISK_LABELS[aiResult.nivel_risco]}</span></p>
                )}
              </div>
            </div>

            {/* Assinatura eletrônica preview */}
            <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex gap-3">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-primary mb-1">Assinatura Eletrônica (Lei 14.063/2020)</p>
                <p className="text-xs text-muted-foreground">
                  Ao concluir, esta evolução será assinada eletronicamente como:<br />
                  <span className="text-foreground font-medium">
                    {user?.full_name || user?.name}
                    {user?.professional_category ? ` — ${user.professional_category === "enfermeiro" ? "Enfermeiro(a)" : user.professional_category === "medico" ? "Médico(a)" : user.professional_category}` : ""}
                    {user?.council_number ? ` — ${user?.state ? `COREN-${user.state}` : "COREN"} ${user.council_number}` : ""}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(`/wounds/${woundId}`)}
                className="flex-1 py-3.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={() => setShowSignModal(true)}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all disabled:opacity-60"
                data-testid="finish-evolution-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                {saving ? "Assinando..." : "Assinar e Salvar"}
              </button>
            </div>
          </div>
        )}

        {/* Modal de confirmação de assinatura */}
        {showSignModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-sm" style={{ fontFamily: "Manrope" }}>Confirmar assinatura eletrônica</h3>
                    <p className="text-xs text-muted-foreground">Esta ação não pode ser desfeita</p>
                  </div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-3 mb-4 border border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-1">A evolução será registrada como:</p>
                  <p className="text-xs text-foreground font-medium leading-relaxed">
                    Assinado eletronicamente por {user?.full_name || user?.name}
                    {user?.council_number ? ` — ${user?.state ? `COREN-${user.state}` : "COREN"} ${user.council_number}` : ""}
                    {" — "}{new Date().toLocaleDateString("pt-BR")} (Horário de Brasília)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Com base na Lei 14.063/2020 — Assinatura Eletrônica em Saúde</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSignModal(false)}
                    className="flex-1 py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all"
                    data-testid="cancel-sign-btn"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => { setShowSignModal(false); handleFinish(); }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all"
                    data-testid="confirm-sign-btn"
                  >
                    <PenLine className="w-4 h-4" />
                    Confirmar e assinar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
